/**
 * Pure Vault state for the Vault screen (DOM-free, unit-tested in vaultModel.test.ts). From save
 * v3 the vault offers 3 resource caches (crafting.md §2.4): a Scale Cache, a Vault Forge Token
 * (one free craft with a Rare floor and +6 Luck) and an Altar Cache (a Sigil, Venom, Moltskin).
 */
import {
  type OpResult,
  type Profile,
  type ResourceBundle,
  type ScaleGrade,
  type VaultOption,
  type VaultOptionKind,
  GRADE_CRAFT_ILVL,
  VAULT_EVERY,
  claimVault,
  gradeForKey,
  vaultOptionBundle,
  vaultPending,
} from '@keystone/progression';
import { SCALE_LABEL } from '../forge/resourceText';

export interface VaultOptionView {
  kind: VaultOptionKind;
  title: string;
  blurb: string;
  /** Resources granted (empty for the token). */
  bundle: ResourceBundle;
  /** Token grade, when this is the Vault Forge Token. */
  tokenGrade: ScaleGrade | null;
}

export interface VaultView {
  /** A choice is waiting. */
  ready: boolean;
  /** Runs counted toward the NEXT vault (0..every-1). Keeps counting while a choice waits. */
  runsInWindow: number;
  runsLeft: number;
  every: number;
  /** runsInWindow / every. */
  fraction: number;
  /** Key levels counted in this window (untimed runs count one lower). */
  windowLevels: number[];
  /** Best key in the window (null when none yet). */
  bestKey: number | null;
  /** Scale grade the next vault would pay right now (from the window so far). */
  projectedGrade: ScaleGrade | null;
  /** The waiting choice (empty when not ready). */
  options: VaultOptionView[];
  /** A legacy (v2 item) vault is waiting: claim it from the old list. */
  legacy: boolean;
}

export function compareVerdict(delta: number, hasCurrent: boolean): 'upgrade' | 'sidegrade' | 'downgrade' | 'empty' {
  if (!hasCurrent) return 'empty';
  return delta > 0.5 ? 'upgrade' : delta < -0.5 ? 'downgrade' : 'sidegrade';
}

export function optionView(o: VaultOption): VaultOptionView {
  if (o.kind === 'token') {
    const g = o.token?.grade ?? 'ashen';
    return { kind: 'token', title: 'Vault Forge Token', blurb: `One free ${SCALE_LABEL[g]} craft (item level ${GRADE_CRAFT_ILVL[g]}): no Fangsteel, Scales or gold, a Rare floor and +6 Luck. Stacks with Venom.`, bundle: {}, tokenGrade: g };
  }
  if (o.kind === 'scales') return { kind: 'scales', title: 'Scale Cache', blurb: 'A pile of Coil Scales and Fangsteel: several temper steps or a craft or two.', bundle: vaultOptionBundle(o), tokenGrade: null };
  return { kind: 'altar', title: 'Altar Cache', blurb: 'An Altar Sigil for a tier piece, plus Venom and Moltskin to steer your next crafts.', bundle: vaultOptionBundle(o), tokenGrade: null };
}

export function vaultView(p: Profile): VaultView {
  const v = p.vault;
  const every = VAULT_EVERY;
  const runsInWindow = Math.max(0, Math.min(every, v.runsInWindow));
  const windowLevels = [...v.windowLevels];
  const bestKey = windowLevels.length ? Math.max(...windowLevels) : null;
  const offer = v.offer ?? null;
  const options = (offer?.options ?? []).map(optionView);
  return {
    ready: vaultPending(p),
    runsInWindow,
    runsLeft: every - runsInWindow,
    every,
    fraction: runsInWindow / every,
    windowLevels,
    bestKey,
    projectedGrade: bestKey === null ? null : gradeForKey(bestKey),
    options,
    legacy: !offer && !!v.pending,
  };
}

/** Claim option `index` (the other choices are discarded). Never throws. */
export function claimVaultChoice(p: Profile, index: number): OpResult {
  const has = p.vault.offer ? !!p.vault.offer.options[index] : !!p.vault.pending?.options[index];
  if (!has) return { ok: false, reason: 'badIndex' };
  try {
    return { ok: true, profile: claimVault(p, index) };
  } catch {
    return { ok: false, reason: 'badIndex' };
  }
}
